// Core
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// MUI
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Components
import Logo from "components/Logo";

function Section({ title, children }) {
  return (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function Paragraphs({ paragraphs, textSecondary }) {
  return paragraphs.map((paragraph) => (
    <Typography key={paragraph} variant="body2" sx={{ color: textSecondary }} mb={2}>
      {paragraph}
    </Typography>
  ));
}

Paragraphs.propTypes = {
  paragraphs: PropTypes.arrayOf(PropTypes.string).isRequired,
  textSecondary: PropTypes.string.isRequired,
};

function BulletList({ items, textSecondary }) {
  return (
    <Box component="ul" sx={{ pl: 3, color: textSecondary }}>
      {items.map((item) => (
        <Typography key={item} component="li" variant="body2" mb={0.75}>
          {item}
        </Typography>
      ))}
    </Box>
  );
}

BulletList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  textSecondary: PropTypes.string.isRequired,
};

function Definitions({ definitions, textSecondary }) {
  return definitions.map(({ term, description }) => (
    <Typography key={term} variant="body2" sx={{ color: textSecondary }} mb={2}>
      <strong>{term}:</strong> {description}
    </Typography>
  ));
}

Definitions.propTypes = {
  definitions: PropTypes.arrayOf(
    PropTypes.shape({
      term: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
  textSecondary: PropTypes.string.isRequired,
};

function TermsAndConditions() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation(["termsAndConditions"]);
  const translatedSections = t("termsAndConditions:sections", { returnObjects: true });
  const translatedContact = t("termsAndConditions:contact", { returnObjects: true });
  const sections = Array.isArray(translatedSections) ? translatedSections : [];
  const contact =
    translatedContact && typeof translatedContact === "object"
      ? translatedContact
      : {
          organization: "",
          addressLine1: "",
          addressLine2: "",
          email: "",
          website: "",
        };
  const isDark = theme.palette.mode === "dark";

  const bg = isDark ? "#0f1419" : "#f8f9fa";
  const surface = isDark ? "#1a2332" : "#ffffff";
  const textPrimary = isDark ? "#f0f4f8" : "#1a2332";
  const textSecondary = isDark ? "#8a98ad" : "#67748e";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";

  return (
    <Box minHeight="100vh" bgcolor={bg} py={4}>
      <Box sx={{ position: "fixed", top: 16, left: 16, zIndex: 10 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
            color: textPrimary,
            "&:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
            },
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Box>

      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 4,
            pt: 2,
          }}
        >
          <Logo />
          <Typography
            variant="h4"
            fontWeight="bold"
            mt={3}
            mb={1}
            sx={{ color: textPrimary, textAlign: "center" }}
          >
            {t("termsAndConditions:title")}
          </Typography>
          <Typography variant="body2" sx={{ color: textSecondary }}>
            {t("termsAndConditions:lastUpdated")}
          </Typography>
        </Box>

        <Box
          sx={{
            background: surface,
            borderRadius: "16px",
            border: `1px solid ${borderColor}`,
            boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.4)" : "0 4px 24px rgba(0,0,0,0.08)",
            p: { xs: 3, md: 6 },
            color: textPrimary,
          }}
        >
          {sections.map((section, index) => (
            <Section key={section.title} title={`${index + 1}. ${section.title}`}>
              {section.paragraphs && (
                <Paragraphs paragraphs={section.paragraphs} textSecondary={textSecondary} />
              )}
              {section.definitions && (
                <Definitions definitions={section.definitions} textSecondary={textSecondary} />
              )}
              {section.items && <BulletList items={section.items} textSecondary={textSecondary} />}
              {section.afterItems && (
                <Paragraphs paragraphs={section.afterItems} textSecondary={textSecondary} />
              )}
            </Section>
          ))}

          <Section title={`17. ${t("termsAndConditions:contact_title")}`}>
            <Typography variant="body2" sx={{ color: textSecondary }} mb={2}>
              {t("termsAndConditions:contact_intro")}
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }} mb={0.75}>
              {contact.organization}
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }} mb={0.75}>
              {contact.addressLine1}
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }} mb={0.75}>
              {contact.addressLine2}
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }} mb={0.75}>
              {t("termsAndConditions:email_label")}{" "}
              <Box
                component="a"
                href={`mailto:${contact.email}`}
                sx={{ color: "#10b981", textDecoration: "none" }}
              >
                {contact.email}
              </Box>
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("termsAndConditions:website_label")}{" "}
              <Box
                component="a"
                href={`https://${contact.website}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#10b981", textDecoration: "none" }}
              >
                {contact.website}
              </Box>
            </Typography>
          </Section>

          <Divider sx={{ mb: 3, borderColor }} />

          <Typography variant="body2" sx={{ color: textSecondary, fontStyle: "italic" }}>
            {t("termsAndConditions:footer")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default TermsAndConditions;
